'use client';
import { useEffect, useState } from 'react';
import {
  getTonesByUserId,
  getFavoriteTonesByUserId,
  getDownloadedTonesByUserId,
} from '@/utils/supabase/queries';
import { ToneCountWithUser, User } from '@/utils/helpers';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/utils/supabase/client';
import { Tones as TonesList } from './Tones';
import { ArchitectureFilter, toProfileTab } from '@/utils/constants';

interface Props {
  user: User;
  pageSize: number;
  isApiSelect?: boolean;
  /** Architecture from OAuth flow when in A2 partner flow; otherwise null. */
  architecture?: ArchitectureFilter | null;
}

const TonesClient = ({ user, pageSize, isApiSelect = false, architecture = null }: Props) => {
  const searchParams = useSearchParams();
  const tab = toProfileTab(searchParams.get('tab'));
  const pageParam = searchParams.get('page');
  const supabase = createClient();
  const [data, setData] = useState<{
    tones: ToneCountWithUser[];
    totalCount: number | null;
    page: number;
    pageSize: number;
    totalPages: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (tab === 'favorites') {
        const data = await getFavoriteTonesByUserId(supabase, user.id, {
          page: pageParam ? parseInt(pageParam) : 1,
          pageSize: pageSize,
        });
        setData(data);
        setLoading(false);
      } else if (tab === 'downloads') {
        const data = await getDownloadedTonesByUserId(supabase, user.id, {
          page: pageParam ? parseInt(pageParam) : 1,
          pageSize: pageSize,
        });
        setData(data);
        setLoading(false);
      } else {
        const data = await getTonesByUserId(supabase, user.id, {
          includePrivate: true,
          // Own profile (ownProfile below), so the viewer is the profile user.
          viewerId: user.id,
          page: pageParam ? parseInt(pageParam) : 1,
          pageSize: pageSize,
        });
        setData(data);
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, pageParam]);

  return (
    <TonesList
      tones={data?.tones || []}
      totalCount={data?.totalCount || 0}
      page={data?.page || 1}
      pageSize={data?.pageSize || pageSize}
      totalPages={data?.totalPages || 1}
      tab={tab}
      ownProfile={true}
      loading={loading}
      isApiSelect={isApiSelect}
      architecture={architecture}
    />
  );
};

export default TonesClient;
